import {useForm} from 'react-hook-form';
import {useId} from 'react';
import {User} from '../../../user';
import {AccountSettingsPanel} from '../account-settings-panel';
import {Button} from '@common/ui/buttons/button';
import {Form} from '@common/ui/forms/form';
import {
  FormTextField
} from '@common/ui/forms/input-field/text-field/text-field';
import {useUpdateAccountDetails} from './update-account-details';
import {Trans} from '@common/i18n/trans';
import {useUploadAvatar} from '../avatar/upload-avatar';
import {useRemoveAvatar} from '../avatar/remove-avatar';
import {FormImageSelector} from '@common/ui/images/image-selector';
import {
  FileUploadProvider
} from '@common/uploads/uploader/file-upload-provider';
import {
  AccountSettingsId
} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {openDialog} from '@common/ui/overlays/store/dialog-store';
import {ConfirmationDialog} from '@common/ui/overlays/dialog/confirmation-dialog';

interface Props {
  user: User;
}

export function BasicInfoPanel({user}: Props) {
  const uploadAvatar = useUploadAvatar({user});
  const removeAvatar = useRemoveAvatar({user});
  const formId = useId();
  const form = useForm<Partial<Omit<User, 'subscriptions'>> & { trustee_email?: string; trustee_name?: string; trustee_first_name?: string; trustee_last_name?: string; mobile_phone?: string }>({
    defaultValues: {
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      trustee_email: (user as any).trustee_email || '',
      trustee_name: (user as any).trustee_name || '',
      trustee_first_name: (user as any).trustee_first_name || '',
      trustee_last_name: (user as any).trustee_last_name || '',
      email: user.email || '',
      mobile_phone: (user as any).mobile_phone || '',
      avatar: user.avatar,
    },
  });
  const updateDetails = useUpdateAccountDetails(form);

  const handleFormSubmit = (newDetails: any) => {
    // Check if ALL required fields are filled
    const requiredFields = [
      { field: 'first_name', label: 'First name' },
      { field: 'last_name', label: 'Last name' },
      { field: 'email', label: 'Email' },
      { field: 'mobile_phone', label: 'Mobile Phone' },
      { field: 'trustee_email', label: 'Trustee Email' },
      { field: 'trustee_first_name', label: 'Trustee First Name' },
      { field: 'trustee_last_name', label: 'Trustee Last Name' },
    ];

    const missingFields = requiredFields.filter(
      ({ field }) => !newDetails[field] || newDetails[field].trim() === ''
    );

    if (missingFields.length > 0) {
      openDialog(ConfirmationDialog, {
        title: <Trans message="Missing Required Fields" />,
        body: <Trans message="Please fill in all required fields (marked with *) before saving." />,
        confirm: <Trans message="OK" />,
        isDanger: false,
      });
      return;
    }

    // All fields populated - proceed with update
    updateDetails.mutate(newDetails);
  };

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.AccountDetails}
      title={<Trans message="Update name and profile image"/>}
      actions={
        <Button
          type="submit"
          variant="flat"
          color="primary"
          form={formId}
          disabled={updateDetails.isPending || !form.formState.isValid}
        >
          <Trans message="Save"/>
        </Button>
      }
    >
      <Form
        form={form}
        className="flex flex-col flex-col-reverse md:flex-row items-center gap-40 md:gap-80"
        onSubmit={handleFormSubmit}
        id={formId}
      >
        <div className="flex-auto w-full">
          <FormTextField
            className="mb-24"
            name="first_name"
            label={<Trans message="First name *"/>}
          />
          <FormTextField
            name="last_name"
            className="mb-24"
            label={<Trans message="Last name *"/>}
          />
          <FormTextField
            name="email"
            className="mb-24"
            label={<Trans message="Email *"/>}
            disabled={true}
          />
          <FormTextField
            name="mobile_phone"
            className="mb-24"
            label={<Trans message="Mobile Phone *"/>}
            type="tel"
          />
          <FormTextField
            name="trustee_email"
            className="mb-24"
            label={<Trans message="Trustee Email *"/>}
          />
          <FormTextField
            name="trustee_first_name"
            className="mb-24"
            label={<Trans message="Trustee First Name *"/>}
          />
          <FormTextField
            name="trustee_last_name"
            className="mb-24"
            label={<Trans message="Trustee Last Name *"/>}
          />
        </div>
        <FileUploadProvider>
          <FormImageSelector
            className="md:mr-80"
            variant="avatar"
            previewSize="w-90 h-90"
            showRemoveButton
            name="avatar"
            diskPrefix="avatars"
            label={<Trans message="Profile image"/>}
            onChange={url => {
              if (url) {
                uploadAvatar.mutate({url});
              } else {
                removeAvatar.mutate();
              }
            }}
          />
        </FileUploadProvider>
      </Form>
    </AccountSettingsPanel>
  );
}
